import { useEffect, useState } from 'react';
import { api, type Plateforme } from '../api/client';

/**
 * Page de présentation — ce que voit une entreprise qui nous découvre.
 *
 * Premier écran avant toute authentification. Son travail est de répondre à
 * trois questions dans l'ordre où elles se posent : qu'est-ce que c'est, en
 * quoi c'est différent, comment j'essaie.
 *
 * **Elle ne promet que ce qui existe.** Chaque affirmation est tenue par du
 * code de ce dépôt — le routage par préfixe, l'idempotence, le grand livre, la
 * liste STOP, le bac à sable déterministe. Une page qui promet au-delà se paie
 * au premier appel d'API.
 *
 * **Et elle s'adapte à la plateforme.** Le libre-service n'existe qu'en test ;
 * en production, un compte s'ouvre auprès d'INFRATEX après intégration.
 *
 * Charte reprise d'`infratex-es.com` : marine profond, accent orange, étiquette
 * en capitales espacées, un mot du titre en orange, grands chiffres.
 */

/** Coordonnées officielles, reprises d'infratex-es.com. */
const CONTACT = {
  societe: 'INFRATEX Energy & Systems',
  adresse: 'Libreville, Gabon',
  telephone: '+241 065 619 058',
  courriel: 'info@infratex-es.com',
  site: 'https://infratex-es.com',
};

const SUJET_DEMANDE = encodeURIComponent('Demande d’accès — plateforme CPaaS SMS');

function Icone({ chemin }: { chemin: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
      <path d={chemin} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const CHEMINS = {
  envoi: 'M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z',
  accuse: 'M20 6 9 17l-5-5',
  bouclier: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
  livre: 'M4 19.5A2.5 2.5 0 0 1 6.5 17H20M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z',
  modele: 'M4 4h16v4H4zM4 12h10M4 17h7',
  equipe: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87',
  crochets: 'M8 4 3 12l5 8M16 4l5 8-5 8',
  horloge: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 6v6l4 2',
};

export function Presentation({
  surConnexion,
  surInscrire,
}: {
  surConnexion: () => void;
  surInscrire: () => void;
}) {
  const [plateforme, setPlateforme] = useState<Plateforme | null>(null);
  useEffect(() => {
    api
      .plateforme()
      .then(setPlateforme)
      .catch(() => undefined);
  }, []);

  /**
   * L'appel à l'action principal n'a pas le même sens selon la plateforme, et
   * tant qu'on ne la connaît pas il n'en a aucun.
   *
   * Le libre-service n'existe qu'en test ; en production un compte s'ouvre
   * auprès d'INFRATEX. Proposer « créer un compte » là où la route refuse
   * mènerait à une impasse — mais proposer « demander un accès » là où
   * l'inscription est ouverte ferait écrire un courriel pour rien. On s'abstient
   * donc de trancher avant de savoir : le bouton reste à sa place, inerte, le
   * temps que `/platform` réponde.
   */
  function ActionPrincipale({ large = false }: { large?: boolean }) {
    const classe = `btn-orange${large ? ' grand' : ''}`;
    if (plateforme === null) {
      return (
        <button type="button" className={classe} disabled>
          Commencer
        </button>
      );
    }
    if (plateforme.signup_enabled) {
      return (
        <button type="button" className={classe} onClick={surInscrire}>
          Créer un compte gratuit
        </button>
      );
    }
    return (
      <a className={classe} href={`mailto:${CONTACT.courriel}?subject=${SUJET_DEMANDE}`}>
        Demander un accès
      </a>
    );
  }

  return (
    <div className="vitrine">
      <header className="vit-entete">
        <div className="dedans">
          <a className="logo" href="#haut">
            <img src="/favicon.svg" alt="" width="34" height="34" />
            <span className="texte">
              <span className="mot">
                Infra<b>tex</b>
              </span>
              <span className="sous">SMPP · CPaaS</span>
            </span>
          </a>
          <nav className="liens">
            <a href="#reseaux">Réseaux</a>
            <a href="#demarrer">Démarrer</a>
            <a href="#fonctions">Fonctions</a>
            <a href="#questions">Questions</a>
          </nav>
          <div className="actions">
            <button type="button" className="btn-fantome" onClick={surConnexion}>
              Se connecter
            </button>
            <ActionPrincipale />
          </div>
        </div>
      </header>

      <main id="haut">
        <section className="vit-heros">
          <div className="dedans">
            <h1>
              Vos SMS au Gabon, <span className="accent">sans intermédiaire</span>.
            </h1>
            <p className="chapo">
              Raccordement direct aux deux réseaux mobiles du pays. Une requête HTTP, et le message
              part — pas d'agrégateur entre vous et l'opérateur, pas de routage à maintenir.
            </p>
            <div className="boutons">
              <ActionPrincipale large />
              <button type="button" className="btn-contour" onClick={surConnexion}>
                J'ai déjà un compte
              </button>
            </div>

            <dl className="chiffres">
              <div>
                <dt>2</dt>
                <dd>réseaux raccordés en direct</dd>
              </div>
              <div>
                <dt>0</dt>
                <dd>agrégateur intermédiaire</dd>
              </div>
              <div>
                <dt>24 h</dt>
                <dd>recrédit d'un message non remis</dd>
              </div>
            </dl>

            {plateforme?.mode === 'test' && (
              <p className="avis">
                Plateforme de test : rien n'atteint un réseau GSM, rien n'est facturé.
              </p>
            )}
          </div>
        </section>

        <section className="vit-bloc" id="reseaux">
          <div className="dedans">
            <p className="surtitre sombre">ACHEMINEMENT</p>
            <h2>
              Le numéro choisit l'opérateur. <span className="accent">Pas vous.</span>
            </h2>
            <p className="intro">
              Au Gabon, les premiers chiffres du numéro désignent le réseau. La plateforme les lit
              et achemine chaque message vers le bon opérateur. Aucune règle à configurer, aucun
              routage à maintenir, et jamais un message envoyé au mauvais réseau.
            </p>

            <div className="cartes trois">
              <article className="carte-vit">
                <span className="rond large">074 · 077</span>
                <h3>Airtel Gabon</h3>
                <p>
                  Les deux préfixes mobiles d'Airtel.
                  <code>+241 77 28 01 50</code>
                </p>
              </article>
              <article className="carte-vit">
                <span className="rond large">062 · 065 · 066</span>
                <h3>Moov Africa / Libertis</h3>
                <p>
                  Les trois préfixes mobiles de Moov.
                  <code>+241 66 00 12 42</code>
                </p>
              </article>
              <article className="carte-vit sobre">
                <span className="rond gris large">autre</span>
                <h3>Préfixe non attribué</h3>
                <p>
                  Ligne fixe, ou préfixe qu'aucun opérateur ne dessert : le message est refusé dès
                  l'acceptation, avant la file et avant toute écriture comptable. Vous ne payez pas
                  un message inacheminable, et vous le savez tout de suite.
                </p>
              </article>
            </div>

            <div className="normalisation">
              <div className="ecritures">
                <p className="titre-mini">Écrivez le numéro comme il vous vient</p>
                <ul>
                  <li>
                    <code>077 28 01 50</code>
                  </li>
                  <li>
                    <code>+241 77 28 01 50</code>
                  </li>
                  <li>
                    <code>24177280150</code>
                  </li>
                  <li>
                    <code>77-28-01-50</code>
                  </li>
                  <li>
                    <code>00241 77.28.01.50</code>
                  </li>
                  <li>
                    <code>77280150</code>
                  </li>
                </ul>
              </div>
              <div className="fleche" aria-hidden="true">
                →
              </div>
              <div className="resultat">
                <p className="titre-mini">Nous en faisons un seul numéro</p>
                <code className="e164">+241 77 28 01 50</code>
                <p className="dit">
                  Indicatif, préfixe <code>00</code>, zéro interurbain, espaces, tirets, points,
                  parenthèses : <strong>tous les formats sont pris en compte</strong>. Le numéro est
                  normalisé une fois pour toutes, et c'est cette forme qui est stockée, facturée et
                  renvoyée dans vos accusés.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="vit-bloc fonce" id="demarrer">
          <div className="dedans deux-colonnes">
            <div>
              <p className="surtitre">INTÉGRATION</p>
              <h2>
                Un envoi tient en <span className="accent">une requête</span>.
              </h2>
              <p className="intro clair">
                API REST, authentification par clé, réponses JSON aux codes stables. L'en-tête{' '}
                <code>Idempotency-Key</code> est accepté sur toute route d'envoi : rejouer une
                requête ne produit jamais un second message, ni un second débit.
              </p>
              <ul className="puces">
                <li>Solde insuffisant, expéditeur non validé, numéro hors réseau : refus immédiat et nommé.</li>
                <li>Rien ne part en file pour échouer plus tard.</li>
                <li>Envoi unitaire ou lot de cent mille, même contrat.</li>
              </ul>
            </div>
            <pre className="code-vit">
              <code>{`curl -X POST https://infratex-smpp.com/v1/messages \\
  -H "Authorization: Bearer ik_live_…" \\
  -H "Idempotency-Key: cmd-2026-4471" \\
  -H "Content-Type: application/json" \\
  -d '{
    "to": "+241 77 28 01 50",
    "from": "MABANQUE",
    "body": "Votre code est 481502.",
    "category": "transactional"
  }'

→ 202 Accepted
{
  "id": "msg_01a15f29-4a40-7b93-…",
  "status": "queued",
  "segments": 1,
  "cost_xaf": 35
}`}</code>
            </pre>
          </div>
        </section>

        <section className="vit-bloc">
          <div className="dedans">
            <p className="surtitre sombre">EN TROIS ÉTAPES</p>
            <h2>
              De la clé au premier SMS, <span className="accent">le jour même</span>.
            </h2>
            <div className="etapes-vit">
              <article>
                <span className="rang">1</span>
                <h3>Ouvrez un compte</h3>
                <p>
                  Vous recevez une clé de test. Elle simule un cycle de vie complet sans toucher un
                  réseau et sans consommer un franc.
                </p>
              </article>
              <article>
                <span className="rang">2</span>
                <h3>Faites valider votre expéditeur</h3>
                <p>
                  Onze caractères à votre marque, enregistrés auprès de l'opérateur. Vos clients
                  lisent votre nom, pas un numéro court.
                </p>
              </article>
              <article>
                <span className="rang">3</span>
                <h3>Passez en production</h3>
                <p>
                  Même code, même contrat d'API. Seule la clé change — et les messages partent pour
                  de bon.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section className="vit-bloc gris" id="fonctions">
          <div className="dedans">
            <p className="surtitre sombre">LA PLATEFORME</p>
            <h2>
              Ce que vous avez <span className="accent">dès l'ouverture</span>.
            </h2>
            <div className="cartes six">
              <article className="carte-vit">
                <span className="icone">
                  <Icone chemin={CHEMINS.envoi} />
                </span>
                <h3>Envoi unitaire et en masse</h3>
                <p>
                  Un message ou cent mille. Les lots sont acceptés en tout ou rien sur le solde : le
                  coût total est connu avant le premier envoi.
                </p>
              </article>
              <article className="carte-vit">
                <span className="icone">
                  <Icone chemin={CHEMINS.accuse} />
                </span>
                <h3>Accusés de réception</h3>
                <p>
                  Le cycle de vie de chaque message, de l'acceptation à la remise au terminal,
                  remonté par l'opérateur lui-même.
                </p>
              </article>
              <article className="carte-vit">
                <span className="icone">
                  <Icone chemin={CHEMINS.crochets} />
                </span>
                <h3>Webhooks signés</h3>
                <p>
                  Vos événements poussés vers votre serveur, signés et rejoués en cas d'échec. Vous
                  n'interrogez rien.
                </p>
              </article>
              <article className="carte-vit">
                <span className="icone">
                  <Icone chemin={CHEMINS.modele} />
                </span>
                <h3>Listes et modèles</h3>
                <p>
                  Carnet de contacts, listes de diffusion, modèles à variables, et l'aperçu du rendu
                  avant envoi — segments facturés compris.
                </p>
              </article>
              <article className="carte-vit">
                <span className="icone">
                  <Icone chemin={CHEMINS.livre} />
                </span>
                <h3>Grand livre et factures</h3>
                <p>
                  Chaque débit et chaque recrédit est une écriture. Factures conformes à l'OHADA,
                  TVA calculée à l'unité près.
                </p>
              </article>
              <article className="carte-vit">
                <span className="icone">
                  <Icone chemin={CHEMINS.equipe} />
                </span>
                <h3>Journal et équipe</h3>
                <p>
                  Chaque message consultable avec son historique. Plusieurs utilisateurs par compte,
                  chacun avec son rôle.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section className="vit-bloc">
          <div className="dedans deux-colonnes">
            <div className="pave">
              <span className="icone grand">
                <Icone chemin={CHEMINS.bouclier} />
              </span>
              <h2>La conformité n'est pas une option.</h2>
              <p>
                Tout message publicitaire est confronté à la liste STOP <strong>avant</strong>{' '}
                émission, et un désabonnement est irréversible. Les messages transactionnels — codes
                de validation, notifications de commande — ne le sont pas : la relation
                contractuelle vaut base légale.
              </p>
              <p className="sobre">
                Déclarer un message publicitaire comme transactionnel est une infraction à la loi
                025/2023. La distinction est portée par l'API, journalisée, et opposable.
              </p>
            </div>
            <div className="pave">
              <span className="icone grand">
                <Icone chemin={CHEMINS.horloge} />
              </span>
              <h2>Une facturation qui se vérifie.</h2>
              <p>
                Montants en francs CFA entiers, sans arrondi flottant. Votre solde se déduit du
                grand livre — il ne se corrige jamais à la main.
              </p>
              <p className="sobre">
                Un message non remis est recrédité automatiquement sous 24 heures. Tarif dégressif
                au volume, consultable depuis la console.
              </p>
            </div>
          </div>
        </section>

        <section className="vit-bloc gris">
          <div className="dedans">
            <p className="surtitre sombre">BAC À SABLE</p>
            <h2>
              Essayez <span className="accent">avant de signer</span>.
            </h2>
            <p className="intro">
              Une clé de test produit un cycle de vie complet — accepté, soumis, remis — sans aucun
              trafic réseau, sans écriture comptable et sans consommer un franc. Les deux derniers
              chiffres du destinataire déterminent l'issue, toujours la même : un test qui passe
              aujourd'hui passe demain.
            </p>
            <ul className="numeros-vit">
              <li>
                <code>+241 66 00 00 00</code>
                <span>remis</span>
              </li>
              <li>
                <code>+241 66 00 00 01</code>
                <span>non remis</span>
              </li>
              <li>
                <code>+241 66 00 00 02</code>
                <span>expiré</span>
              </li>
              <li>
                <code>+241 66 00 00 03</code>
                <span>rejeté</span>
              </li>
            </ul>
          </div>
        </section>

        <section className="vit-bloc" id="questions">
          <div className="dedans etroit">
            <p className="surtitre sombre">QUESTIONS FRÉQUENTES</p>
            <h2>
              Ce qu'on nous demande <span className="accent">le plus souvent</span>.
            </h2>
            <div className="faq">
              <details>
                <summary>Faut-il un contrat avec chaque opérateur ?</summary>
                <p>
                  Non. INFRATEX porte les raccordements à Airtel Gabon et Moov Gabon. Vous avez un
                  seul contrat, une seule API, une seule facture.
                </p>
              </details>
              <details>
                <summary>Combien de temps pour envoyer mon premier message ?</summary>
                <p>
                  Le bac à sable est accessible immédiatement avec une clé de test. Le passage en
                  production dépend de la validation de votre expéditeur par l'opérateur, qui est
                  leur délai, pas le nôtre.
                </p>
              </details>
              <details>
                <summary>Que se passe-t-il si un message n'est pas remis ?</summary>
                <p>
                  L'accusé de l'opérateur le dit, et le montant est recrédité automatiquement. Vous
                  ne payez que ce qui est parti. Un message refusé avant émission n'est jamais
                  débité.
                </p>
              </details>
              <details>
                <summary>Puis-je envoyer depuis mon nom de marque ?</summary>
                <p>
                  Oui, sur onze caractères au maximum — c'est la limite de la norme SMPP, pas la
                  nôtre. L'expéditeur doit être validé par l'opérateur avant le premier envoi en
                  production.
                </p>
              </details>
              <details>
                <summary>Comment sont gérés les désabonnements ?</summary>
                <p>
                  La liste STOP est tenue par la plateforme et consultée avant toute émission
                  publicitaire. Un désabonnement est irréversible et opposable, conformément à la
                  loi 025/2023.
                </p>
              </details>
              <details>
                <summary>Et si je dépasse mon débit autorisé ?</summary>
                <p>
                  Rien n'est perdu. Les messages patientent en file et repartent au rythme accordé.
                  Un dépassement ne provoque ni perte ni double facturation.
                </p>
              </details>
            </div>
          </div>
        </section>

        <section className="vit-appel">
          <div className="dedans">
            <h2>
              Parlons de votre <span className="accent">volume</span>.
            </h2>
            <p>
              Notre équipe est à Libreville. Décrivez votre cas d'usage, nous vous répondons avec
              une grille tarifaire et un accès de test.
            </p>
            <div className="boutons">
              <ActionPrincipale large />
              <a className="btn-contour" href={`tel:${CONTACT.telephone.replace(/\s/g, '')}`}>
                {CONTACT.telephone}
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="vit-pied">
        <div className="dedans">
          <div className="colonnes">
            <div className="marque-pied">
              <span className="signe">
                <img src="/favicon.svg" alt="INFRATEX" width="30" height="30" />
                <span className="mot">
                  Infra<b>tex</b>
                </span>
              </span>
              <p>
                Plateforme CPaaS SMS A2P pour le marché gabonais. Raccordement direct à Airtel Gabon
                et Moov Gabon.
              </p>
            </div>
            <div>
              <h4>PRODUIT</h4>
              <a href="#reseaux">Acheminement</a>
              <a href="#demarrer">Intégration</a>
              <a href="#fonctions">Fonctions</a>
              <a href="#questions">Questions fréquentes</a>
            </div>
            <div>
              <h4>ACCÈS</h4>
              <button type="button" className="lien-pied" onClick={surConnexion}>
                Console
              </button>
              <a href={`mailto:${CONTACT.courriel}?subject=${SUJET_DEMANDE}`}>Demander un accès</a>
              <a href={CONTACT.site} target="_blank" rel="noreferrer">
                infratex-es.com ↗
              </a>
            </div>
            <div>
              <h4>CONTACT</h4>
              <span>{CONTACT.societe}</span>
              <span>{CONTACT.adresse}</span>
              <a href={`tel:${CONTACT.telephone.replace(/\s/g, '')}`}>{CONTACT.telephone}</a>
              <a href={`mailto:${CONTACT.courriel}`}>{CONTACT.courriel}</a>
            </div>
          </div>
          <div className="bas">
            <span>© {new Date().getFullYear()} {CONTACT.societe}. Tous droits réservés.</span>
            <button type="button" className="lien-pied" onClick={surConnexion}>
              Accéder à la console
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
